/**
 * Checkout order-summary quantity stepper.
 */

import { showToast } from './toast.js';

let pending = false;

function getConfig() {
	return window.doneCommerce || {};
}

async function apiFetch(path, options = {}) {
	const config = getConfig();
	const response = await fetch(`${config.restUrl}${path}`, {
		credentials: 'same-origin',
		...options,
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': config.nonce,
			...(options.headers || {}),
		},
	});

	const payload = await response.json().catch(() => null);

	if (!response.ok) {
		const error = new Error(payload?.message || 'Request failed');
		error.status = response.status;
		throw error;
	}

	return payload;
}

function updateCartCounts(count) {
	document.querySelectorAll('[data-cart-count]').forEach((el) => {
		el.textContent = String(count);
		el.dataset.count = String(count);
		el.hidden = Number(count) <= 0;
	});
}

function refreshCheckout() {
	if (typeof window.jQuery !== 'undefined') {
		window.jQuery(document.body).trigger('update_checkout');
	}
}

/**
 * @param {HTMLButtonElement} button
 */
function nextQuantity(button) {
	const item = button.closest('.dc-review-item');
	const countEl = item?.querySelector('.dc-review-item__qty');
	if (!item || !countEl) {
		return null;
	}

	const delta = Number(button.dataset.checkoutQtyDelta);
	const current = Number.parseInt(countEl.textContent || '1', 10);
	const min = Number.parseInt(button.dataset.min || '1', 10);
	const maxRaw = button.dataset.max;
	const max = maxRaw === undefined || maxRaw === '' ? Infinity : Number.parseInt(maxRaw, 10);
	const key = button.dataset.cartKey || '';

	if (!key || !Number.isFinite(delta) || !Number.isFinite(current)) {
		return null;
	}

	let next = current + delta;

	if (delta > 0) {
		if (Number.isFinite(max) && next > max) {
			return null;
		}
		return { key, next };
	}

	if (next >= min) {
		return { key, next };
	}

	const name = button.dataset.productName || 'this product';
	const template = getConfig().i18n?.removeFromOrder || 'Remove %s from your order?';
	if (!window.confirm(template.replace('%s', name))) {
		return null;
	}

	return { key, next: 0 };
}

/**
 * @param {{ key: string, next: number }} change
 * @param {HTMLButtonElement} button
 */
async function commitQuantity(change, button) {
	const badge = button.closest('.dc-review-item')?.querySelector('.dc-review-item__qty');
	if (badge && change.next > 0) {
		badge.textContent = String(change.next);
	}

	pending = true;
	document.body.classList.add('dc-checkout-qty-pending');

	try {
		const data = await apiFetch(`done-commerce/v1/cart/items/${encodeURIComponent(change.key)}`, {
			method: 'POST',
			body: JSON.stringify({ quantity: change.next }),
		});
		const count = Number(data?.count ?? 0);
		updateCartCounts(count);

		if (count <= 0) {
			window.location.href = getConfig().cartUrl || '/cart/';
			return;
		}

		refreshCheckout();
	} catch (error) {
		const message = error?.message || getConfig().i18n?.cartUpdateFailed || 'Unable to update cart';
		showToast(message, { variant: 'error' });
		refreshCheckout();
	} finally {
		pending = false;
		document.body.classList.remove('dc-checkout-qty-pending');
	}
}

export function initCheckoutQty() {
	if (!document.body.classList.contains('woocommerce-checkout')) {
		return;
	}

	document.addEventListener('click', (event) => {
		const button = event.target.closest('[data-checkout-qty-delta]');
		if (!(button instanceof HTMLButtonElement) || pending || button.disabled) {
			return;
		}

		event.preventDefault();

		const change = nextQuantity(button);
		if (!change) {
			return;
		}

		void commitQuantity(change, button);
	});
}
